<script setup>
import { ref, computed, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);

const filteredOptions = computed(() => {
  const searchTerm = search.value.toLowerCase();
  return props.options.filter(option =>
    option.label.toLowerCase().includes(searchTerm)
  );
});

const selectedLabel = computed(() => {
  const selected = props.options.find(
    option => option.value === props.modelValue
  );
  return selected?.label || props.placeholder;
});

const toggleOpen = () => {
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => dropdownRef.value?.focus());
  }
};

const selectOption = option => {
  emit('update:modelValue', option.value);
  open.value = false;
  search.value = '';
};
</script>

<template>
  <div class="relative flex items-center justify-end">
    <OnClickOutside @trigger="open = false">
      <Button
        variant="ghost"
        :color="hasError ? 'ruby' : 'slate'"
        size="sm"
        trailing-icon
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        :label="selectedLabel"
        no-animation
        class="!h-auto !py-1 !px-2 -me-2 max-w-[14rem] font-normal"
        :class="{
          '!text-n-slate-9': !modelValue && !hasError,
          'animate-shake': hasError,
        }"
        @click="toggleOpen"
      />

      <ComboBoxDropdown
        ref="dropdownRef"
        v-model:search-value="search"
        :open="open"
        :options="filteredOptions"
        :selected-values="modelValue"
        class="!w-64 !end-0"
        @select="selectOption"
      />
    </OnClickOutside>
  </div>
</template>
